/**
 * service-mail mail-errors client. Mirrors:
 *   apps/service-mail/src/modules/mail-errors/mail-errors.controller.ts
 *
 * RBAC: all calls require Mail/manageErrors.
 */
import { env } from '../env';

import { request } from './client';

export type MailErrorSource = 'SCANNER' | 'OCR' | 'VALIDATION' | 'PARTNER_INTEGRATION' | 'OTHER';
export type MailErrorSubjectType = 'MAIL_ITEM' | 'DRAFT' | 'OTHER';
export type MailErrorStatus = 'OPEN' | 'INVESTIGATING' | 'RESOLVED' | 'DISMISSED';

export interface MailErrorDto {
  readonly id: string;
  readonly source: MailErrorSource;
  readonly kind: string;
  readonly subjectType: MailErrorSubjectType;
  readonly subjectId: string | null;
  readonly outletCode: string | null;
  readonly operatorId: string;
  readonly details: string;
  readonly payload: unknown;
  readonly status: MailErrorStatus;
  readonly assignedToOperatorId: string | null;
  readonly resolvedByOperatorId: string | null;
  readonly resolvedAt: string | null;
  readonly resolutionNotes: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ListMailErrorsResponse {
  readonly items: readonly MailErrorDto[];
  // eslint-disable-next-line upmp/no-number-money -- row count
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface CreateMailErrorBody {
  readonly source: MailErrorSource;
  readonly kind: string;
  readonly subjectType: MailErrorSubjectType;
  readonly subjectId?: string;
  readonly outletCode?: string;
  readonly details: string;
  readonly payload: unknown;
}

export interface ListMailErrorsQuery {
  readonly status?: MailErrorStatus;
  readonly source?: MailErrorSource;
  readonly outletCode?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

const baseUrl = (): string => env.serviceMailBaseUrl;

export async function createMailError(body: CreateMailErrorBody): Promise<MailErrorDto> {
  return request<MailErrorDto>(baseUrl(), '/v1/mail-errors', { method: 'POST', body });
}

export async function listMailErrors(query: ListMailErrorsQuery = {}): Promise<ListMailErrorsResponse> {
  return request<ListMailErrorsResponse>(baseUrl(), '/v1/mail-errors', {
    method: 'GET',
    query: {
      status: query.status,
      source: query.source,
      outletCode: query.outletCode,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function assignMailError(id: string, operatorId: string): Promise<MailErrorDto> {
  return request<MailErrorDto>(baseUrl(), `/v1/mail-errors/${id}/assign`, {
    method: 'POST',
    body: { operatorId },
  });
}

export async function resolveMailError(id: string, notes: string): Promise<MailErrorDto> {
  return request<MailErrorDto>(baseUrl(), `/v1/mail-errors/${id}/resolve`, {
    method: 'POST',
    body: { notes },
  });
}

export async function dismissMailError(id: string, notes: string): Promise<MailErrorDto> {
  return request<MailErrorDto>(baseUrl(), `/v1/mail-errors/${id}/dismiss`, {
    method: 'POST',
    body: { notes },
  });
}
